import React, { useEffect, useState } from 'react';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';

import { Heading, Spinner, Button, Line } from '@mm/components';

import { EmailInput } from '../../../../../common/modals/InviteUserModal/EmailInput/EmailInput';

import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import { openNewConfirmModal } from '@mm/state/modules/modalsState/actions';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { useSupportItemsCRUDActions } from '@mm/state/actions/SupportItemsCRUDActions';
import css from './ContactUsSlideoutMenu.module.css';
import DemoCardImage from './demo-card-image.svg';
import Diamonds from './diamonds.svg';
import { CloseIcon } from '@mm/icons';
import { SendOutlined } from '@ant-design/icons';
import { SlideOutMenuType } from '@mm/state/interfaces/view';

const formatString = (s = '', { length }: { length?: number } = {}) => {
  const esc = s.replace?.(/&#8232;/g, ' ');

  if (!length) {
    return esc;
  }

  return esc.slice(0, length) + (s.length > length ? '...' : '');
};

type Tabs = 'guides' | 'sendMessage';

export type ContactUsSlideoutMenuProps = {
  menuType?: SlideOutMenuType;
};

export const ContactUsSlideoutMenu = ({
  menuType,
}: ContactUsSlideoutMenuProps) => {
  const supportItems = useSelector(
    (state: StoreState) => state.supportItems.entities,
  );
  const currentUser = useSelector((state: StoreState) => state.session.session);

  const busy = useSelector((state: StoreState) => state.supportItems.busy);
  const [currentTab, setCurrentTab] = useState<Tabs>('guides');

  const [email, setEmail] = useState(currentUser.email);
  const [message, setMessage] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(!!currentUser.email);
  const { retrieveAll, publish } = useSupportItemsCRUDActions();
  const [isMessageSent, setIsMessageSent] = useState(false);
  const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

  const bookDemoUrl =
    'https://calendly.com/mobilemonkey-sales/customers-ai-x-ray-demo?utm_source=inproduct&utm_medium=slideout&utm_campaign=helpicon';
  const canSubmit = message.length !== 0 && isValidEmail;

  useEffect(() => {
    const fetchSupportItems = async () => {
      await retrieveAll();
    };

    if (supportItems.length === 0) {
      fetchSupportItems();
    }
  }, []);

  const onBeforeClose = () => {
    if (message.length !== 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => reject(),
        onResolve: async close => {
          close();
          resolve(true);
        },
        resolveButton: {
          children: "Yes, I'm sure",
        },
      });
    });
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(
    onBeforeClose,
    menuType,
  );

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const sendMessage = async () => {
    const success = await publish(email, message, fullName);
    if (success) {
      setCurrentTab('guides');
      setIsMessageSent(true);
    }
  };

  return (
    <Menu className={css.menu} setRef={setRef}>
      <Menu.Header
        onClose={close}
        closeIcon="outlinedClose"
        className={css.header}
        closeIconClassName={css.header__close_icon}
      >
        <Heading size={3}>Hi {fullName}, how can we help you?</Heading>
      </Menu.Header>
      <Menu.Content className={css.menu_content}>
        {isMessageSent && (
          <div className={css.message_sent}>
            <CloseIcon
              className={css.message_sent__close_icon}
              onClick={() => setIsMessageSent(false)}
            />
            <div className={css.message_sent_content}>
              <img src={Diamonds} />+
              <div className={css.message_sent_text}>
                <Heading size={3}>We’ve get your request.</Heading>
                <p>A member of our team will reach out shortly.</p>
              </div>
            </div>
          </div>
        )}
        {currentTab === 'guides' && (
          <>
            <div className={css.menu_content_section}>
              <div className={css.features_block}>
                <div className={css.articles_header}>
                  <Heading size={3}>New Features</Heading>
                </div>
                <div className={css.features}>
                  {supportItems.slice(0, 2).map(item => (
                    <div
                      key={item.id}
                      className={css.feature}
                      onClick={() => window.open(item.url, '_blank')}
                    >
                      {item.iconUrl ? (
                        <img className={css.feature_icon} src={item.iconUrl} />
                      ) : (
                        <img
                          className={css.feature_image}
                          src={item.attachmentUrl}
                        />
                      )}
                      <div className={css.feature_text}>
                        <Heading className={css.feature_title} size={3}>
                          {formatString(item.title)}
                        </Heading>
                        <div className={css.feature_description}>
                          {formatString(item.description, { length: 80 })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <Line className={css.line} />
            <div className={css.menu_content_section}>
              <div className={css.articles}>
                <div className={css.articles_header}>
                  <Heading size={3}>New Articles</Heading>
                  <a
                    href="https://customers.ai/blog"
                    target="_blank"
                    rel="noreferrer"
                    className={css.articles_header_link}
                  >
                    See more articles
                  </a>
                </div>
                <div className={css.articles_list}>
                  {supportItems.slice(2, 6).map(item => (
                    <div
                      key={item.id}
                      className={css.article}
                      onClick={() => window.open(item.url, '_blank')}
                    >
                      {(item.attachmentUrl || item.iconUrl) && (
                        <img
                          className={css.article_image}
                          src={item.attachmentUrl || item.iconUrl}
                        />
                      )}
                      <Heading className={css.articles_description} size={2}>
                        {formatString(item.title, { length: 80 })}
                      </Heading>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <Line className={css.line} />
            <div className={css.menu_content_section}>
              <div className={css.support}>
                <Button
                  size="lg"
                  css={{ container: css.button }}
                  onClick={() => setCurrentTab('sendMessage')}
                >
                  Talk to Support
                </Button>
                <div className={css.demo_card}>
                  <img className={css.demo_card__image} src={DemoCardImage} />
                  <p className={css.demo_card__text}>
                    Talk to an expert to take advantage of all our features
                  </p>
                  <Button
                    color="secondary-blue"
                    onClick={() => window.open(bookDemoUrl, '_blank')}
                    css={{ container: css.demo_card__button }}
                  >
                    Book a demo
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {currentTab === 'sendMessage' && (
          <div className={css.send_message}>
            <textarea
              placeholder="Let us know about any issues you face or ideas to improve your workflow"
              value={message}
              onChange={e => setMessage(e.target.value)}
              className={css.send_message__textarea}
            />
            <label className={css.send_message__input}>
              <span>Your email</span>
              <EmailInput
                withIcon={false}
                defaultValue={email}
                onChange={payload => {
                  if (payload.isValid) {
                    setEmail(payload.email);
                    setIsValidEmail(true);
                  } else {
                    setIsValidEmail(false);
                  }
                }}
              />
            </label>
            <Button
              disabled={!canSubmit}
              leftIcon={<SendOutlined />}
              css={{ container: css.send_message__button }}
              onClick={sendMessage}
            >
              {busy && <Spinner show standalone />}
              {!busy && 'Send'}
            </Button>
          </div>
        )}
        {busy && <Spinner show standalone={true} />}
      </Menu.Content>
    </Menu>
  );
};
